// components
import EmployeeHeader from '../../components/EmployeeHeader';
import EmployeeCell from '../../components/EmployeeCell';
import OpenRequestFunctionCell from '../../components/OpenRequestFunctionCell';

// hooks
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// styles
import { StyledTypographyNormalLineHeightSemiBold } from 'components/shared/Typography/Typography.styles';

// types
import type { GridColDef, GridValidRowModel } from '@mui/x-data-grid-pro';
import type { EmployeeNameFormatTypes } from '../../RosterPlan.types';

// utils
import { formatEmployeeName } from '../useGetColumns.utils';

/**
 * The columns that identify a row: the optional sort order number, and the name column — the
 * employee for the employees grid, the function for the open requests grid.
 *
 * Both grids get exactly one name column, which is why they share a hook: the two are alternatives
 * of the same slot, not two independent columns.
 */
const useIdentityColumns = ({
  isOpenRequests = false,
  showSortOrderColumn = false,
  showSourceCostCenter,
  showCommitmentCostCenter,
  employeeNameFormat
}: {
  isOpenRequests?: boolean;
  showSortOrderColumn?: boolean;
  showSourceCostCenter?: boolean;
  showCommitmentCostCenter?: boolean;
  employeeNameFormat?: EmployeeNameFormatTypes;
}) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'rosterPlan'
  });

  return useMemo<GridColDef<GridValidRowModel>[]>(
    () => [
      ...(showSortOrderColumn
        ? [
            {
              field: 'sortorder',
              headerName: '#',
              width: 50,
              align: 'center',
              headerAlign: 'center'
            } as GridColDef<GridValidRowModel>
          ]
        : []),
      ...(isOpenRequests
        ? [
            {
              field: 'functionName',
              headerName: t('dataGrid.functions'),
              minWidth: 220,
              flex: 1,
              renderHeader: () => (
                <StyledTypographyNormalLineHeightSemiBold variant="subtitle1">
                  {t('dataGrid.functions')}
                </StyledTypographyNormalLineHeightSemiBold>
              ),
              renderCell: (params) => (
                <OpenRequestFunctionCell params={params} />
              )
            } as GridColDef<GridValidRowModel>
          ]
        : [
            {
              field: 'employee',
              headerName: t('dataGrid.employee'),
              minWidth: 220,
              flex: 1,
              valueGetter: (_, row) =>
                formatEmployeeName(row, employeeNameFormat),
              renderHeader: () => (
                <EmployeeHeader
                  showSourceCostCenter={showSourceCostCenter}
                  showCommitmentCostCenter={showCommitmentCostCenter}
                />
              ),
              renderCell: (params) => (
                <EmployeeCell
                  params={params}
                  showSourceCostCenter={showSourceCostCenter}
                  showCommitmentCostCenter={showCommitmentCostCenter}
                  employeeNameFormat={employeeNameFormat}
                />
              )
            } as GridColDef<GridValidRowModel>
          ])
    ],
    [
      t,
      isOpenRequests,
      showSortOrderColumn,
      showSourceCostCenter,
      showCommitmentCostCenter,
      employeeNameFormat
    ]
  );
};

export default useIdentityColumns;
